(() => {
  const roots = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const parseDate = (value) => {
    const timestamp = Date.parse(value || '');
    return Number.isFinite(timestamp) ? timestamp : 0;
  };

  const sortChapters = (list) => {
    const chapters = Array.from(list.querySelectorAll('[data-event-chapter]'));

    chapters.sort((a, b) => {
      const statusA = a.dataset.eventStatus;
      const statusB = b.dataset.eventStatus;
      const dateA = parseDate(a.dataset.eventDate);
      const dateB = parseDate(b.dataset.eventDate);

      if (statusA !== statusB) {
        if (statusA === 'upcoming') return -1;
        if (statusB === 'upcoming') return 1;
      }

      if (statusA === 'upcoming' && statusB === 'upcoming') {
        return dateA - dateB;
      }

      if (statusA === 'past' && statusB === 'past') {
        return dateB - dateA;
      }

      return dateB - dateA;
    });

    chapters.forEach((chapter, index) => {
      const number = String(index + 1).padStart(2, '0');
      chapter.classList.toggle('deeb-events-index__chapter--reverse', index % 2 === 1);
      chapter.classList.toggle('deeb-events-index__chapter--forward', index % 2 === 0);
      chapter.querySelectorAll('[data-event-number]').forEach((node) => {
        node.textContent = number;
      });
      list.appendChild(chapter);
    });

    return chapters;
  };

  const initFilters = (root, chapters, onChange) => {
    const buttons = Array.from(root.querySelectorAll('[data-events-filter-kind]'));
    if (!buttons.length) return () => {};

    const emptyState = root.querySelector('[data-events-filter-empty]');
    const emptyText = root.querySelector('[data-events-filter-empty-text]');
    const listeners = [];

    buttons.forEach((button) => {
      const handler = () => {
        const kind = button.dataset.eventsFilterKind;
        const value = button.dataset.eventsFilterValue;
        let matchesCount = 0;

        buttons.forEach((candidate) => {
          candidate.setAttribute('aria-pressed', String(candidate === button));
        });

        chapters.forEach((chapter) => {
          let matches = true;
          if (kind === 'status') matches = chapter.dataset.eventStatus === value;
          chapter.hidden = !matches;
          if (matches) matchesCount += 1;
        });

        if (emptyState) {
          emptyState.hidden = matchesCount > 0;
        }

        if (emptyText && matchesCount === 0) {
          emptyText.textContent = value === 'upcoming'
            ? 'No upcoming events are announced yet.'
            : value === 'past'
              ? 'No past events are listed yet.'
              : 'No events are listed yet.';
        }

        if (typeof onChange === 'function') onChange();
      };

      button.addEventListener('click', handler);
      listeners.push(() => button.removeEventListener('click', handler));
    });

    return () => listeners.forEach((remove) => remove());
  };

  const initMotion = (root, chapters) => {
    if (!chapters.length) return { cleanup: () => {}, refresh: () => {} };

    let activeChapter = chapters.find((chapter) => !chapter.hidden) || chapters[0];
    let frame = 0;

    const markActive = (chapter) => {
      if (!chapter || chapter.hidden) return;
      activeChapter = chapter;
      chapters.forEach((candidate) => {
        candidate.classList.toggle('is-active', candidate === chapter);
      });
    };

    const update = () => {
      frame = 0;
      if (!desktop.matches || reduceMotion.matches || !activeChapter || activeChapter.hidden) return;

      const rect = activeChapter.getBoundingClientRect();
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const travel = Math.max(rect.height - viewport, 1);
      const progress = clamp((-rect.top) / travel, 0, 1);
      const signed = progress - 0.5;
      const direction = activeChapter.classList.contains('deeb-events-index__chapter--reverse') ? -1 : 1;

      activeChapter.style.setProperty('--events-progress', progress.toFixed(4));
      activeChapter.style.setProperty('--events-portrait-y', `${(signed * -28).toFixed(2)}px`);
      activeChapter.style.setProperty('--events-portrait-x', `${(signed * 12 * direction).toFixed(2)}px`);
      activeChapter.style.setProperty('--events-portrait-scale', (1.015 - Math.abs(signed) * 0.018).toFixed(4));
      activeChapter.style.setProperty('--events-glow-y', `${(signed * 38).toFixed(2)}px`);
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    if (!('IntersectionObserver' in window)) {
      markActive(activeChapter);
      return {
        refresh: () => {
          const visible = chapters.find((chapter) => !chapter.hidden);
          markActive(visible);
        },
        cleanup: () => {},
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting && !entry.target.hidden)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible[0]) {
          markActive(visible[0].target);
          requestUpdate();
        }
      },
      {
        rootMargin: '-28% 0px -38% 0px',
        threshold: [0, 0.1, 0.25, 0.5, 0.75],
      }
    );

    chapters.forEach((chapter) => observer.observe(chapter));
    markActive(activeChapter);
    requestUpdate();

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    desktop.addEventListener('change', requestUpdate);
    reduceMotion.addEventListener('change', requestUpdate);

    const refresh = () => {
      const visible = chapters.find((chapter) => !chapter.hidden);
      markActive(visible);
      requestUpdate();
    };

    return {
      refresh,
      cleanup: () => {
      observer.disconnect();
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      desktop.removeEventListener('change', requestUpdate);
      reduceMotion.removeEventListener('change', requestUpdate);
      if (frame) window.cancelAnimationFrame(frame);
      },
    };
  };

  const init = (root) => {
    if (!root || roots.has(root)) return;

    const list = root.querySelector('[data-events-list]');
    const chapters = list ? sortChapters(list) : [];
    const motion = initMotion(root, chapters);
    const cleanupFilters = initFilters(root, chapters, motion.refresh);

    roots.set(root, () => {
      cleanupFilters();
      motion.cleanup();
      roots.delete(root);
    });
  };

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-deeb-events-index]')) init(scope);
    scope.querySelectorAll?.('[data-deeb-events-index]').forEach(init);
  };

  initAll();

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-deeb-events-index]')
      ? event.target
      : event.target.querySelector('[data-deeb-events-index]');
    const cleanup = root ? roots.get(root) : null;
    if (cleanup) cleanup();
  });
})();
